import { Box, useTheme } from '@mui/material';
import { styled } from '@mui/system';
import React, { useMemo, useState } from 'react';
import { Handle, Position, useNodes, useStore } from 'reactflow';
import { handleDragHighlight } from '../../Canvas/utils/handleDragHighlight';
import { portTypeKey } from '../../Canvas/utils/portType';

const COMPAT_GREEN = '#22c55e';

const handleChrome = {
  idle: {
    background: '#555',
    width: 12,
    height: 12,
    border: '2px solid #fff',
    boxShadow: 'none',
    opacity: 1,
  },
  origin: {
    background: '#555',
    width: 14,
    height: 14,
    border: '2px solid #fff',
    boxShadow: `0 0 0 3px rgba(34, 197, 94, 0.35)`,
    opacity: 1,
  },
  compatible: {
    background: COMPAT_GREEN,
    width: 16,
    height: 16,
    border: '2px solid #fff',
    boxShadow: `0 0 0 4px rgba(34, 197, 94, 0.45)`,
    opacity: 1,
  },
  incompatible: {
    background: '#555',
    width: 12,
    height: 12,
    border: '2px solid #fff',
    boxShadow: 'none',
    opacity: 0.32,
  },
};

const HandleWrapper = styled('div')(({ position, index, total, highlight }) => {
  const along = `${((index + 1) / (total + 1)) * 100}%`;
  const vertical = position === Position.Top || position === Position.Bottom;
  return {
    position: 'absolute',
    zIndex: highlight === 'compatible' ? 20 : 5,
    ...(vertical
      ? {
          [position === Position.Top ? 'top' : 'bottom']: -10,
          left: along,
          transform: 'translateX(-50%)',
        }
      : {
          [position === Position.Left ? 'left' : 'right']: -10,
          top: along,
        }),
  };
});

const HandleTooltip = styled(Box)({
  position: 'absolute',
  top: -22,
  padding: '2px 4px',
  borderRadius: '4px',
  fontSize: 9,
  whiteSpace: 'nowrap',
});

const HandleComponent = ({
  nodeId,
  type,
  position,
  id,
  schema,
  index,
  total,
  packageType,
  isConnectable = true,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const theme = useTheme();
  const label = portTypeKey(schema) || packageType || id;
  const nodes = useNodes();
  const connectionNodeId = useStore((s) => s.connectionNodeId);
  const connectionHandleId = useStore((s) => s.connectionHandleId);
  const connectionHandleType = useStore((s) => s.connectionHandleType);

  const highlight = useMemo(
    () =>
      handleDragHighlight({
        connectionNodeId,
        connectionHandleId,
        connectionHandleType,
        handleNodeId: nodeId,
        handleId: id,
        handleType: type,
        nodes,
      }),
    [
      connectionNodeId,
      connectionHandleId,
      connectionHandleType,
      nodeId,
      id,
      type,
      nodes,
    ]
  );

  const chrome = handleChrome[highlight] || handleChrome.idle;
  const showLabel = isHovered || highlight === 'compatible';

  return (
    <HandleWrapper
      position={position}
      index={index}
      total={total}
      highlight={highlight}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <Handle
        type={type}
        position={position}
        id={id}
        isConnectable={isConnectable}
        aria-label={`${type === 'source' ? 'output' : 'input'} ${id}`}
        className={
          highlight === 'compatible' ? 'canvas-handle-compatible' : undefined
        }
        style={{
          background: chrome.background,
          width: chrome.width,
          height: chrome.height,
          border: chrome.border,
          boxShadow: chrome.boxShadow,
          opacity: chrome.opacity,
          transition:
            'background 120ms ease, box-shadow 120ms ease, width 120ms ease, height 120ms ease, opacity 120ms ease',
        }}
      />
      {showLabel && (
        <HandleTooltip
          right={
            position === Position.Left || position === Position.Top
              ? 'auto'
              : 0
          }
          left={
            position === Position.Left || position === Position.Top ? 0 : 'auto'
          }
          sx={{
            ...(position === Position.Top ? { top: 16 } : null),
            ...(position === Position.Bottom ? { top: 'auto', bottom: 16 } : null),
            background:
              highlight === 'compatible'
                ? COMPAT_GREEN
                : theme.palette.grey[900] || '#333',
            color: theme.palette.common.white || '#fff',
          }}
        >
          {id}: {label}
        </HandleTooltip>
      )}
    </HandleWrapper>
  );
};

export default HandleComponent;
